import { WarehouseAPI } from '../services/api';

let cachedExcludedShelves: string[] | null = null;

export const getExcludedShelvesCached = async (): Promise<string[]> => {
  if (cachedExcludedShelves !== null) {
    return cachedExcludedShelves;
  }
  try {
    const response = await WarehouseAPI.getExcludedShelves();
    cachedExcludedShelves = response;
  } catch (err) {
    console.error("Failed to fetch excluded shelves from API, using fallback:", err);
    // Fallback list
    cachedExcludedShelves = [
      "NOT_FOUND",
      "D2C-CONSUMPTION",
      "B2B-CONSUMPTION",
      "CARDING-CONSUMPTION",
      "DEFAULT",
      "B2B",
      "Blinkit",
      "DAMAGE",
      "LOVEBOX-INSERT",
      "MNEW",
      "QC_REJECTED"
    ];
  }
  return cachedExcludedShelves;
};

/**
 * Get total inventory quantity for a SKU across all shelves using the shelf-wise inventory API
 * @param skuCode - The SKU code to check inventory for
 * @returns Promise<number> - Total quantity across all shelf locations
 */
export const getTotalInventoryForSKUFromAPI = async (skuCode: string): Promise<number> => {
  try {
    // Get all inventory items for this SKU (with a large limit to get all results)
    const response = await WarehouseAPI.getShelfWiseInventory(skuCode, 0, 1000);


    // Check if response.data exists and is an array
    if (!response.data || !Array.isArray(response.data)) {
      return 0;
    }

    const excludedShelves = await getExcludedShelvesCached();
    const isExcluded = (shelfCode: string) => {
      if (!shelfCode) return false;
      const code = shelfCode.toUpperCase();
      return excludedShelves.some(item => {
        const upperItem = item.toUpperCase();
        return code === upperItem || code.includes(upperItem);
      });
    };

    // Sum up quantities from all shelf locations for this SKU
    // Only count GOOD_INVENTORY items that exactly match the SKU code and are not excluded
    const totalQuantity = response.data.reduce((total, item) => {
      // Only count items that exactly match the SKU code, are good inventory, and not on excluded shelves
      if (item.skuCode.toLowerCase() === skuCode.toLowerCase() &&
        item.inventoryType === 'GOOD_INVENTORY' &&
        !isExcluded(item.shelfCode)) {
        return total + item.quantity;
      }
      return total;
    }, 0);

    return totalQuantity;
  } catch (error) {
    console.error(`❌ Error fetching inventory for SKU ${skuCode}:`, error);
    // Re-throw the error so we can see what's actually going wrong
    throw error;
  }
};

/**
 * Get inventory availability for multiple SKUs at once
 * @param skuCodes - Array of SKU codes to check
 * @returns Promise<Record<string, number>> - Object with SKU codes as keys and quantities as values
 */
export const getMultipleSKUInventory = async (skuCodes: string[]): Promise<Record<string, number>> => {
  const inventoryMap: Record<string, number> = {};

  // Execute all inventory checks in parallel for better performance
  const inventoryPromises = skuCodes.map(async (skuCode) => {
    const quantity = await getTotalInventoryForSKUFromAPI(skuCode);
    return { skuCode, quantity };
  });

  const results = await Promise.all(inventoryPromises);

  results.forEach(({ skuCode, quantity }) => {
    inventoryMap[skuCode] = quantity;
  });

  return inventoryMap;
};

/**
 * Aggregates a list of child SKUs by their SKU ID, summing up the required quantities.
 * @param childSKUs - Array of child SKUs with quantities
 * @returns Array of aggregated child SKUs
 */
export const getAggregatedChildSKUs = <T extends { skuId: string; requiredQuantity: number }>(
  childSKUs: T[]
): T[] => {
  const aggregatedMap: Record<string, T> = {};

  for (const child of childSKUs) {
    if (aggregatedMap[child.skuId]) {
      aggregatedMap[child.skuId] = {
        ...aggregatedMap[child.skuId],
        requiredQuantity: aggregatedMap[child.skuId].requiredQuantity + child.requiredQuantity
      };
    } else {
      aggregatedMap[child.skuId] = { ...child };
    }
  }

  return Object.values(aggregatedMap);
};

/**
 * Calculate maximum possible bundles based on child SKU inventory
 * @param childSKUs - Array of child SKUs with required quantities
 * @returns Promise<number> - Maximum number of bundles that can be created
 */
export const calculateMaxPossibleBundles = async (
  childSKUs: Array<{ skuId: string; requiredQuantity: number }>
): Promise<number> => {
  try {
    // Aggregate child SKUs first (in case of duplicates)
    const aggregatedChildSKUs = getAggregatedChildSKUs(childSKUs);
    const skuCodes = aggregatedChildSKUs.map(child => child.skuId);
    const inventoryMap = await getMultipleSKUInventory(skuCodes);

    let maxPossible = Infinity;

    for (const childSKU of aggregatedChildSKUs) {
      const availableQuantity = inventoryMap[childSKU.skuId] || 0;
      const possibleForThisSKU = Math.floor(availableQuantity / childSKU.requiredQuantity);
      maxPossible = Math.min(maxPossible, possibleForThisSKU);
    }

    return maxPossible === Infinity ? 0 : Math.max(0, maxPossible);
  } catch (error) {
    console.error('❌ Error calculating max possible bundles:', error);
    throw error;
  }
};

/**
 * Validate stock availability for bundle creation
 * @param childSKUs - Array of child SKUs with required quantities
 * @param bundleQuantity - Number of bundles to create
 * @returns Promise with validation result including availability details
 */
export const validateBundleStock = async (
  childSKUs: Array<{ skuId: string; name: string; requiredQuantity: number }>,
  bundleQuantity: number
) => {
  try {
    // Aggregate child SKUs first (in case of duplicates)
    const aggregatedChildSKUs = getAggregatedChildSKUs(childSKUs);
    const skuCodes = aggregatedChildSKUs.map(child => child.skuId);
    const inventoryMap = await getMultipleSKUInventory(skuCodes);

    const childSKUAvailability = aggregatedChildSKUs.map(childSKU => {
      const availableQuantity = inventoryMap[childSKU.skuId] || 0;
      const requiredTotal = childSKU.requiredQuantity * bundleQuantity;
      const isAvailable = availableQuantity >= requiredTotal;

      return {
        skuId: childSKU.skuId,
        name: childSKU.name,
        requiredQuantity: requiredTotal,
        availableQuantity,
        isAvailable,
        shelfLocations: [] // We'll populate this separately if needed
      };
    });

    const allAvailable = childSKUAvailability.every(item => item.isAvailable);
    const errors = childSKUAvailability
      .filter(item => !item.isAvailable)
      .map(item => `Insufficient stock for ${item.skuId}: need ${item.requiredQuantity}, have ${item.availableQuantity}`);

    return {
      isValid: allAvailable,
      errors,
      childSKUAvailability
    };
  } catch (error) {
    console.error('❌ Error validating bundle stock:', error);
    // Re-throw to see actual error
    throw error;
  }
};

/**
 * Enhanced validation that includes detailed shelf location information
 * @param childSKUs - Array of child SKUs with required quantities
 * @param bundleQuantity - Number of bundles to create
 * @returns Promise with detailed validation result including shelf locations
 */
export const validateBundleStockWithShelfDetails = async (
  childSKUs: Array<{ skuId: string; name: string; requiredQuantity: number }>,
  bundleQuantity: number
) => {
  try {
    // First get the basic validation
    const basicValidation = await validateBundleStock(childSKUs, bundleQuantity);

    // Then enhance with shelf location details
    const enhancedAvailability = await Promise.all(
      basicValidation.childSKUAvailability.map(async (availability) => {
        try {
          // Get shelf-wise inventory for this SKU
          const shelfInventory = await WarehouseAPI.getShelfWiseInventory(
            availability.skuId,
            0,
            100 // Get up to 100 shelf locations
          );

          // Convert to the format expected by the UI (InventoryItem format)
          // Only include GOOD_INVENTORY items that match the SKU exactly
          const shelfLocations = shelfInventory.data
            .filter(item =>
              item.skuCode.toLowerCase() === availability.skuId.toLowerCase() &&
              item.inventoryType === 'GOOD_INVENTORY'
            )
            .map((item) => ({
              skuId: item.skuCode,
              shelfLocation: item.shelfCode, // Map shelfCode to shelfLocation
              quantity: item.quantity,
              lastUpdated: item.updatedAt
            }));

          return {
            ...availability,
            shelfLocations
          };
        } catch (shelfError) {
          console.error(`❌ Error fetching shelf details for ${availability.skuId}:`, shelfError);
          return {
            ...availability,
            shelfLocations: [] // Empty if we can't get shelf details
          };
        }
      })
    );

    return {
      ...basicValidation,
      childSKUAvailability: enhancedAvailability
    };
  } catch (error) {
    console.error('❌ Error validating bundle stock with shelf details:', error);
    throw error;
  }
};